import * as Crypto from "expo-crypto";

import i18n from "@/i18n";
import {
  FALLBACK_NOUNS,
  MAX_DISPLAY_NAME_LENGTH,
  MIN_DISPLAY_NAME_LENGTH,
  composeGeneratedName,
} from "@/lib/generated-name";

// Re-exported so every existing importer keeps its import path. The values
// live in `generated-name.ts` because that file imports nothing and can
// therefore be driven by a real test; this one pulls `@/i18n` and cannot.
export { MAX_DISPLAY_NAME_LENGTH, MIN_DISPLAY_NAME_LENGTH };

const LINK_PATTERN =
  /https?:\/\/|www\.|\b[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.(?:com|net|org|io|co|app|me|xyz|gg|tv|dev|edu|gov|uk|us)\b/i;
const ALLOWED_DISPLAY_NAME_PATTERN = /^[\p{L}\p{M}\p{N} .'’-]+$/u;

export function collapseDisplayName(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

export function validateDisplayName(raw: string): string | undefined {
  if (!raw) return i18n.t("profile.validation.required");
  if (raw.trim() !== raw) return i18n.t("profile.validation.trim");
  if (raw.includes("  ")) return i18n.t("profile.validation.singleSpace");
  if ((raw.match(/ /g) ?? []).length > 1)
    return i18n.t("profile.validation.maxOneSpace");

  const words = raw.split(" ").filter(Boolean);
  if (words.length > 2) return i18n.t("profile.validation.maxTwoWords");
  if (raw.length < MIN_DISPLAY_NAME_LENGTH) {
    return i18n.t("profile.validation.minLength", {
      min: MIN_DISPLAY_NAME_LENGTH,
    });
  }
  if (raw.length > MAX_DISPLAY_NAME_LENGTH) {
    return i18n.t("profile.validation.lengthRange", {
      min: MIN_DISPLAY_NAME_LENGTH,
      max: MAX_DISPLAY_NAME_LENGTH,
    });
  }

  if (raw.includes("@")) return i18n.t("profile.validation.noHandle");
  if (LINK_PATTERN.test(raw)) return i18n.t("profile.validation.noLink");
  if ((raw.match(/\d/g) ?? []).length >= 7) {
    return i18n.t("profile.validation.noPhone");
  }
  if (!ALLOWED_DISPLAY_NAME_PATTERN.test(raw)) {
    return i18n.t("profile.validation.allowed");
  }

  return undefined;
}

function translatedWordList(key: string, fallback: string[]): string[] {
  const value = i18n.t(key, { returnObjects: true });
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.values(value).every(
      (item) => typeof item === "string" && item.trim().length > 0,
    )
  ) {
    return Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([, item]) => item);
  }
  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((item) => typeof item === "string" && item.trim().length > 0)
  ) {
    return value;
  }
  return fallback;
}

export function generateDisplayName(seed?: string | null): string {
  // `Crypto.randomUUID()`, not `Date.now()` and `Math.random()`.
  //
  // The discriminator is the whole reason a generated name is rare, so its
  // entropy is load-bearing rather than cosmetic. `Math.random()` is a
  // non-cryptographic PRNG — xorshift under Hermes — and `Date.now()` is
  // largely predictable, so seeding 35 bits of user-visible identity from that
  // pair asks a weak source for the one property the design depends on.
  //
  // The seeded call is unaffected: it passes a user id, which is already a v4
  // UUID, and stays deterministic so the onboarding suggestion is stable.
  const source = seed?.trim() || Crypto.randomUUID();
  const nouns = translatedWordList("profile.generatedName.nouns", FALLBACK_NOUNS);

  return composeGeneratedName(nouns, source);
}
